import {
  flow,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from 'mobx-state-tree';

import { Reduced } from '../../types';
import { withEnvironment } from '../extensions/with-environment';
import _ from 'lodash';
import { ExploreElementType } from 'spinnaxBase/data/explore';
import { logger } from 'services/Logger';

// Custom Date type für Firestore-Timestamps, Strings und Date-Objekte
const SafeDate = types.custom<Date, any>({
  name: 'SafeDate',
  fromSnapshot(value: any) {
    if (!value) return new Date();
    if (value instanceof Date && !isNaN(value.getTime())) return value;
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d;
    }
    // Firestore Timestamp: muss Objekt sein, aber nicht Date
    if (
      typeof value === 'object' &&
      value !== null &&
      !(value instanceof Date) &&
      typeof (value as any).seconds === 'number'
    ) {
      return new Date(
        (value as any).seconds * 1000 + ((value as any).nanoseconds || 0) / 1e6
      );
    }
    if (typeof value === 'number') {
      return new Date(value);
    }
    return new Date();
  },
  toSnapshot(value) {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    // Fallback: immer valides Date-Objekt zurückgeben
    return new Date();
  },
  isTargetType(value) {
    return value instanceof Date;
  },
  getValidationMessage(value) {
    if (value instanceof Date && !isNaN(value.getTime())) return '';
    return 'Not a valid date';
  },
});

// Ranking Entry Model für einzelne Ranking-Einträge
export const RankingEntryModel = types.model('RankingEntry', {
  userId: types.string,
  userName: types.string,
  score: types.number,
  position: types.optional(types.number, 0), // Position im Ranking (1, 2, 3, etc.)
  profileImageUrl: types.optional(types.string, ''),
  nationality: types.optional(types.string, ''),
});

// Contest Participant Model für Teilnehmer in Contests
export const ContestParticipantModel = types.model('ContestParticipant', {
  userId: types.string,
  userName: types.string,
  joinedAt: SafeDate, // Zeitpunkt des Beitritts
  runCount: types.optional(types.number, 0), // Anzahl der bereits durchgeführten Runs
  bestScore: types.optional(types.number, 0), // Bester Score des Teilnehmers
  scores: types.optional(types.array(types.number), []), // Liste der Scores
  nationality: types.optional(types.string, ''),
  profileImageUrl: types.optional(types.string, ''),
  lastRunAt: types.optional(types.maybeNull(types.Date), null), // Zeitpunkt des letzten Runs
});

export const _ExploreElementModel = types
  .model({
    id: types.identifier,
    name: types.string,
    type: types.enumeration(
      'ExploreElementType',
      Object.values(ExploreElementType) as string[]
    ),

    // Owner-Identifikation - uid des Benutzers, der das Element erstellt hat
    uid: types.optional(types.string, ''),

    // Event-spezifische Felder
    startDate: types.Date,
    endDate: types.Date,

    favorite: types.optional(types.boolean, false), // not written to firebase storage
    country: types.optional(types.string, ''),
    gender: types.optional(types.string, ''),
    age: types.optional(types.number, 0),
    location: types.optional(types.string, ''),
    nationality: types.optional(types.string, ''),
    description: types.optional(types.string, ''),
    // Koordinaten für Karten-Integration
    latitude: types.optional(types.maybeNull(types.number), null),
    longitude: types.optional(types.maybeNull(types.number), null),

    // Rankings - Liste von Ranking-Einträgen für Group/Ranking-Typen
    rankings: types.optional(types.array(RankingEntryModel), []),
    // Ranking-Metadaten
    rankingCategory: types.optional(types.string, ''), // z.B. "Best Runs 2024", "Regional Championship"
    rankingPeriod: types.optional(types.string, ''), // z.B. "2024", "Q1 2024", "Monthly"
    maxParticipants: types.optional(types.number, 0), // Maximale Anzahl Teilnehmer (für Rankings und Contests)
    isLive: types.optional(types.boolean, false), // Ob das Ranking live aktualisiert wird
    isPublic: types.optional(types.boolean, true), // Ob das Ranking öffentlich ist

    // Contest-spezifische Felder
    maxRuns: types.optional(types.number, 3), // Maximale Anzahl Runs pro Teilnehmer
    contestRules: types.optional(types.string, ''), // Contest-Regeln/Beschreibung
    isContestActive: types.optional(types.boolean, false), // Ob der Contest aktiv ist

    // Contest-Teilnehmer
    participants: types.optional(types.array(ContestParticipantModel), []), // Liste der Teilnehmer
    isParticipationOpen: types.optional(types.boolean, true), // Ob Teilnahme noch möglich ist
  })
  .volatile((_self) => {
    return {};
  })
  .extend(withEnvironment)
  .actions((self) => ({
    afterCreate: flow(function* () {}),
    toggleFavorite() {
      self.favorite = !self.favorite;
    },
    setFavorite(val: boolean) {
      self.favorite = val;
    },

    // Contest-spezifische Actions
    joinContest(
      userId: string,
      userName: string,
      nationality?: string,
      profileImageUrl?: string
    ) {
      // Prüfen ob User bereits teilnimmt
      const existingParticipant = self.participants.find(
        (p) => p.userId === userId
      );
      if (existingParticipant) {
        return false;
      }

      // Prüfen ob Contest voll ist
      if (
        self.maxParticipants > 0 &&
        self.participants.length >= self.maxParticipants
      ) {
        return false;
      }

      // Prüfen ob Teilnahme noch offen ist
      if (!self.isParticipationOpen) {
        return false;
      }

      // Teilnehmer hinzufügen
      self.participants.push({
        userId,
        userName,
        joinedAt: new Date(),
        runCount: 0,
        bestScore: 0,
        scores: [],
        nationality: nationality || '',
        profileImageUrl: profileImageUrl || '',
        lastRunAt: null,
      });

      return true;
    },

    leaveContest(userId: string) {
      const participantIndex = self.participants.findIndex(
        (p) => p.userId === userId
      );
      if (participantIndex !== -1) {
        self.participants.splice(participantIndex, 1);
        return true;
      }
      return false;
    },

    updateContestParticipantRun(userId: string, newScore: number): boolean {
      try {
        const participant = self.participants.find((p) => p.userId === userId);

        if (!participant) {
          return false;
        }

        // ✅ FIXED: Detailed logging before and after update
        const beforeUpdate = {
          runCount: participant.runCount,
          bestScore: participant.bestScore,
          scoresCount: participant.scores.length,
          scores: [...participant.scores],
        };

        // ✅ Update participant data
        participant.runCount = (participant.runCount || 0) + 1;
        participant.scores.push(newScore);
        participant.bestScore = Math.max(participant.bestScore || 0, newScore);
        participant.lastRunAt = new Date();

        const afterUpdate = {
          runCount: participant.runCount,
          bestScore: participant.bestScore,
          scoresCount: participant.scores.length,
          scores: [...participant.scores],
        };

        return true;
      } catch (error) {
        logger.error('❌ Failed to update contest participant:', error);
        return false;
      }
    },

    createContestParticipant(
      userId: string,
      userName: string,
      nationality?: string,
      profileImageUrl?: string
    ): boolean {
      const existingParticipant = self.participants.find(
        (p) => p.userId === userId
      );
      if (existingParticipant) {
        return false;
      }

      self.participants.push({
        userId,
        userName,
        joinedAt: new Date(),
        runCount: 0,
        bestScore: 0,
        scores: [],
        nationality: nationality || '',
        profileImageUrl: profileImageUrl || '',
        lastRunAt: null,
      });
      return true;
    },

    updateParticipantScore(userId: string, newScore: number): boolean {
      return this.updateContestParticipantRun(userId, newScore);
    },
  }))
  .views((self) => ({
    // Prüft, ob der angegebene Benutzer der Besitzer dieses Elements ist
    isOwnedByUser(userUid: string | null | undefined) {
      return userUid === self.uid && self.uid !== '';
    },
    isUserParticipating(userId: string): boolean {
      if (self.participants) {
        const inParticipants = self.participants.some(
          (p) => p.userId === userId
        );
        if (inParticipants) return true;
      }
      if ((self as any).rankings) {
        const rankings = (self as any).rankings;
        const inRankings = rankings.some((r: any) => r.userId === userId);
        if (inRankings) return true;
      }

      // Check contestParticipants (legacy support)
      if ((self as any).contestParticipants) {
        const contestParticipants = (self as any).contestParticipants;
        const inContestParticipants = contestParticipants.some(
          (cp: any) => cp.userId === userId
        );
        if (inContestParticipants) return true;
      }

      return false;
    },

    getParticipantCount() {
      return self.participants.length;
    },

    getTopParticipants(limit = 10) {
      return self.participants
        .slice()
        .sort((a, b) => b.bestScore - a.bestScore)
        .slice(0, limit);
    },

    isContestFull() {
      return (
        self.maxParticipants > 0 &&
        self.participants.length >= self.maxParticipants
      );
    },

    getParticipant(userId: string) {
      return self.participants.find((p) => p.userId === userId);
    },

    getParticipantRunCount(userId: string): number {
      const participant = self.participants.find((p) => p.userId === userId);
      return participant?.runCount || 0;
    },

    getParticipantBestScore(userId: string): number {
      const participant = self.participants.find((p) => p.userId === userId);
      return participant?.bestScore || 0;
    },
  }));

type NotInFirebase =
  | 'playData'
  | 'favorite'
  | 'recordData'
  | 'localVideoFileName'
  | 'localVideoUri';

export const ExploreElementModel = _ExploreElementModel
  .named('ExploreElementModel')
  .views((self) => {
    return {
      toServer(uid: string) {
        // ✅ FIXED: Create complete server object with all participant data
        const serverData = {
          id: self.id,
          name: self.name,
          type: self.type,
          uid: uid,
          startDate: self.startDate,
          endDate: self.endDate,
          country: self.country,
          gender: self.gender,
          age: self.age,
          location: self.location,
          nationality: self.nationality,
          description: self.description,
          latitude: self.latitude,
          longitude: self.longitude,
          isPublic: self.isPublic,

          // ✅ Contest-specific fields
          maxRuns: self.maxRuns,
          contestRules: self.contestRules,
          isContestActive: self.isContestActive,
          isParticipationOpen: self.isParticipationOpen,
          maxParticipants: self.maxParticipants,
          isLive: self.isLive,

          // ✅ FIXED: Include complete participants data
          participants: self.participants.map(participant => ({
            userId: participant.userId,
            userName: participant.userName,
            joinedAt: participant.joinedAt,
            runCount: participant.runCount,
            bestScore: participant.bestScore,
            scores: participant.scores.slice(), // ✅ Copy array to ensure it's included
            nationality: participant.nationality,
            profileImageUrl: participant.profileImageUrl,
            lastRunAt: participant.lastRunAt,
          })),

          // ✅ Rankings data
          rankings: self.rankings.map(ranking => ({
            userId: ranking.userId,
            userName: ranking.userName,
            score: ranking.score,
            position: ranking.position,
            profileImageUrl: ranking.profileImageUrl,
            nationality: ranking.nationality,
          })),
          rankingCategory: self.rankingCategory,
          rankingPeriod: self.rankingPeriod,
          lastUpdated: new Date().toISOString(),
        };

        return serverData;
      },
    };
  });

// HINWEIS: ContestModal und andere Modals dürfen KEIN plain JS-Objekt bekommen, sondern immer ein echtes Model-Objekt (ExploreElementModel)!

export type ExploreElement = Reduced<Instance<typeof ExploreElementModel>>;
export type ExploreElementSnapshotIn = SnapshotIn<typeof ExploreElementModel>;
export type ExploreElementIn = Reduced<
  Omit<ExploreElementSnapshotIn, NotInFirebase>
>;
export type ExploreElementOut = Omit<
  SnapshotOut<typeof ExploreElementModel>,
  NotInFirebase
>;
export type ExploreElementCreateIn = Omit<ExploreElementIn, 'id'> & {
  uid: string;
};
